<template>
  <div class="ai_voice_generator">
    <form
      class="formkit-form"
      id="aiVoiceGeneratorForm"
      name="aiVoiceGeneratorForm"
    >
      <div class="form-section">
        <h3 class="section-title" style="color: rgba(8, 34, 57, 1); font-weight: 800;">Type/paste your script</h3>
        <div class="script-textarea-container">
          <textarea
            class="script-textarea"
            v-model="scriptContent"
            placeholder="Enter your script here..."
            @input="countWords"
            :class="{ 'error-input': scriptError }"
          ></textarea>
          <div class="word-counter">{{ wordCount }}/80 words</div>
          <div v-if="scriptError" class="error-message">{{ scriptError }}</div>
        </div>
      </div>

      <div class="form-section">
        <h3 class="section-title" style="color: rgba(8, 34, 57, 1); font-weight: 800;">Voice gender</h3>
        <div class="custom-dropdown" ref="customDropdown" :class="{ 'error-input': genderError }">
          <div class="dropdown-selected" @click="toggleDropdown">
            <span>{{ selectedGenderText }}</span>
            <span class="dropdown-arrow">▼</span>
          </div>
          <div class="dropdown-options" v-show="dropdownOpen">
            <div 
              v-for="option in genderOptions" 
              :key="option.id" 
              class="dropdown-option"
              @click="selectGender(option.id)"
            >
              {{ option.name }}
            </div>
          </div>
        </div>
        <div v-if="genderError" class="error-message">{{ genderError }}</div>
      </div>
      
      <button
        class="generate-button"
        @click.prevent="generateVoices"
      >
        GENERATE AI VOICES
      </button>
    </form>
  </div>
</template>

<script>
import { mdiHumanMaleFemale, mdiTextBox } from "@mdi/js";

export default {
  name: 'AIVoiceGenerator',
  data() {
    return {
      mdiHumanMaleFemale,
      mdiTextBox,
      scriptContent: "",
      selectedGender: "",
      wordCount: 0,
      dropdownOpen: false,
      scriptError: "",
      genderError: "",
      genderOptions: [
        { id: "male", name: "Male" },
        { id: "female", name: "Female" }
      ]
    };
  },
  
  computed: {
    selectedGenderText() {
      if (!this.selectedGender) {
        return "Choose a voice gender";
      }
      const option = this.genderOptions.find(opt => opt.id === this.selectedGender);
      return option ? option.name : "Choose a voice gender";
    }
  },
  
  methods: {
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },
    
    selectGender(id) {
      this.selectedGender = id;
      this.dropdownOpen = false;
    },
    
    countWords() {
      if (!this.scriptContent) {
        this.wordCount = 0;
        return;
      }
      
      const words = this.scriptContent.trim().split(/\s+/);
      this.wordCount = words.length;
    },
    
    generateVoices() {
      this.scriptError = "";
      this.genderError = "";
      
      if (!this.scriptContent.trim()) {
        this.scriptError = "Please enter a script";
        return;
      }
      
      if (!this.selectedGender) {
        this.genderError = "Please select a voice gender";
        return;
      }
      
      const words = this.scriptContent.trim().split(/\s+/);
      if (words.length > 80) {
        this.scriptError = "Please limit your script to 80 words";
        return;
      }
      
      const genderMap = {
        "male": "1000",
        "female": "1001",
      };
      
      const genderCode = genderMap[this.selectedGender] || "1000";
      
      const encodedScript = btoa(encodeURIComponent(this.scriptContent));
      
      const forceLocal = false;
      
      const isProduction = window.location.hostname.includes('voice123.com') && !window.location.hostname.includes('sandbox');
      const isSandbox = window.location.hostname.includes('sandbox.voice123.com');
      const isLocalhost = forceLocal || window.location.hostname.includes('localhost') || window.location.hostname.includes('127.0.0.1');
      
      let domain;
      let pathPrefix = '';
      if (isProduction) {
        domain = 'studio.voice123.com';
        pathPrefix = '/app';
      } else if (isSandbox) {
        domain = 'studio.sandbox.voice123.com';
        pathPrefix = '/app';
      } else {
        domain = 'localhost:5173';
      }
      
      const protocol = isLocalhost ? 'http' : 'https';
      const redirectUrl = `${protocol}://${domain}${pathPrefix}/ai/preview?script=${encodedScript}&gender=${genderCode}`;
      
      window.location.href = redirectUrl;
    },
    
    handleClickOutside(event) {
      const dropdown = this.$refs.customDropdown;
      if (dropdown && !dropdown.contains(event.target) && this.dropdownOpen) {
        this.dropdownOpen = false;
      }
    }
  },
  
  mounted() {
    // Add click outside listener to close dropdown when clicked outside
    document.addEventListener('click', this.handleClickOutside);
  },
  
  beforeDestroy() {
    // Remove event listener when component is destroyed
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped lang="scss">
.error-input {
  border: 1px solid #FF4136 !important;
}

.error-message {
  color: #FF4136;
  font-size: 12px;
  margin-top: 5px;
  font-weight: 600;
}

.ai_voice_generator {
  width: 100%;
  max-width: 415px; /* Desktop default width */
  margin: 0 auto;
  padding: 30px;
  background: #ffffff;
  border-radius: 15px;
  border: 1.74px solid rgba(0, 0, 0, 0.1);
  
  /* Tablet styles */
  @media screen and (max-width: 1024px) and (min-width: 768px) {
    max-width: 650px;
    border-radius: 17.82px;
    border: 2.06px solid rgba(0, 0, 0, 0.1);
  }
  
  /* Mobile styles - keep it responsive */
  @media screen and (max-width: 767px) {
    max-width: 90%;
    padding: 20px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .form-section {
    margin-bottom: 20px !important;
    
    .section-title {
      font-size: 18px !important;
      font-weight: 600 !important;
      margin-bottom: 10px !important;
      color: rgba(0, 0, 0, 0.87) !important;
    }
  }
  
  .script-textarea-container {
    position: relative !important;
    margin-bottom: 15px !important;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
  }
  
  .script-textarea {
    width: 100% !important;
    height: 200px !important;
    padding: 15px !important;
    border: 1px solid rgba(0, 172, 193, 1) !important;
    border-radius: 10px !important;
    font-size: 16px !important;
    resize: none !important;
    color: rgba(0, 0, 0, 0.87) !important;
    background-color: white !important;
    
    &:focus {
      outline: none !important;
      border-color: rgba(0, 172, 193, 1) !important;
    }
  }
  
  .word-counter {
    position: absolute !important;
    bottom: 10px !important;
    right: 15px !important;
    font-size: 14px !important;
    color: rgba(0, 0, 0, 0.54) !important;
  }
  
  /* Custom dropdown styles */
  .custom-dropdown {
    position: relative !important;
    width: 100% !important;
    font-family: 'Muli', sans-serif !important;
  }
  
  .dropdown-selected {
    width: 100% !important;
    height: 50px !important;
    padding: 0 20px !important;
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    border: 1px solid rgba(0, 172, 193, 1) !important;
    border-radius: 10px !important;
    background-color: white !important;
    cursor: pointer !important;
    font-size: 16px !important;
    color: rgba(0, 0, 0, 0.87) !important;
  }
  
  .dropdown-arrow {
    color: #555 !important;
    font-size: 12px !important;
  }
  
  .dropdown-options {
    position: absolute !important;
    top: 100% !important;
    left: 0 !important;
    width: 100% !important;
    border: 1px solid rgba(0, 172, 193, 1) !important;
    border-top: none !important;
    border-radius: 0 0 10px 10px !important;
    background-color: white !important;
    z-index: 10 !important;
    max-height: 200px !important;
    overflow-y: auto !important;
  }
  
  .dropdown-option {
    padding: 12px 20px !important;
    cursor: pointer !important;
    transition: background-color 0.2s !important;
  }
  
  .dropdown-option:hover {
    background-color: rgba(0, 172, 193, 0.1) !important;
  }
  
  /* Hide default arrows in IE */
  .simple-select::-ms-expand {
    display: none !important;
  }
  
  .generate-button {
    display: block !important;
    width: 100% !important;
    max-width: 300px !important;
    margin: 30px auto 0 !important;
    font-weight: 700 !important;
    height: 50px !important;
    line-height: 50px !important;
    text-align: center !important;
    border-radius: 4px !important;
    text-transform: uppercase !important;
    transition: background-color 0.3s, color 0.3s !important;
    border: none !important;
    cursor: pointer !important;
    background-color: #FFAE00 !important;
    color: rgba(0, 63, 117, 1) !important;
    font-size: 16px !important;
    letter-spacing: 0.5px !important;
    
    &:hover {
      background-color: darken(#FFAE00, 10%) !important;
      color: #fff !important;
    }
  }
  
  // Responsive styles
  @media only screen and (max-width: 600px) {
    .ai_voice_generator {
      padding: 10px !important;
      
      .script-textarea {
        min-height: 120px !important;
      }
      
      .generate-button {
        max-width: 100% !important;
      }
    }
  }
}
</style>
